import { Subject, firstValueFrom } from 'rxjs'

import { Crepe, CrepeConfig } from '@milkdown/crepe'
import { EditorStatus } from '@milkdown/core'
import { commonmark } from "@milkdown/kit/preset/commonmark"
import { gfm } from "@milkdown/kit/preset/gfm"
import { upload } from "@milkdown/plugin-upload"
import { clipboard } from "@milkdown/plugin-clipboard"

//import type { Ctx } from '@milkdown/kit/ctx'
//import { GroupBuilder } from 'node_modules/@milkdown/crepe/lib/types/utils'
//import { TopBarItem } from 'node_modules/@milkdown/crepe/lib/types/feature/top-bar/config'
//import { emoji } from "@milkdown/plugin-emoji"

import mermaid from 'mermaid';
//import { mermaidPlugin } from "prosemirror-mermaid";
//import type { MermaidConfig } from "mermaid";
//import { createLowlight } from "lowlight";

import { CerRandom } from '@cer/cer-util/cer-random';

// Definér en node til callouts (Note, Info, Warning)

export class CerMarkdownEdit {

  public destroy() {
    this.teardown();
  }

  private teardown() {
    if (this.editor != null) {
      this.editor.destroy();
    }
  }

  private element: HTMLElement;
  private markdown: string;
  public editDocument(element: HTMLElement, markdown: string) : boolean {
    if (this.element != element || this.markdown != markdown) {
      this.element = element;
      this.markdown = markdown;
      this.teardown();
      this.edit();
      return true;
    }
    else {
      return false;
    }
  }

  public getMarkdown(): string {
    return this.editor?.getMarkdown();
  }

  private editor: Crepe = null;
  private edit() {
    const cfg = this.editorConfig();
    cfg.root = this.element;
    cfg.defaultValue = this.markdown;
    var e = new Crepe(cfg);
    e.editor
      .use(commonmark)
      .use(gfm)
      /*.use(emoji)*/
      .use(clipboard)
      .use(upload)
      .onStatusChange((e) => this.onStatusChange(e));
    e.create()
      .then(() => {
        this.editor = e;
      });
    return this.editor;
  }

  public editorConfig(): CrepeConfig {
    return {
      features: {
        [Crepe.Feature.CodeMirror]: true,
        [Crepe.Feature.Cursor]: false,
        [Crepe.Feature.ListItem]: true,
        [Crepe.Feature.LinkTooltip]: true,
        [Crepe.Feature.ImageBlock]: true,
        [Crepe.Feature.BlockEdit]: true,
        [Crepe.Feature.Placeholder]: true,
        [Crepe.Feature.Toolbar]: true,
        [Crepe.Feature.Table]: true,
        [Crepe.Feature.Latex]: false,
        [Crepe.Feature.TopBar]: true,
        [Crepe.Feature.AI]: false,
      },
      featureConfigs: {
        [Crepe.Feature.CodeMirror]: {
          /*  languages: [ 
              { name: 'mermaid', alias: ['Diagram' ], extensions: ['.mer'], filename: undefined, 
                 loadFunc: () => {return null}), load: async () => {  { return null} },  }
            ],*/
          searchPlaceholder: 'Søg kodesprog',
          noResultText: 'Ingen fundet',
          previewLabel: 'Visning',
          previewOnlyByDefault: true,
          previewToggleText: (previewOnlyMode) => previewOnlyMode ? 'Rediger kode' : 'Skjul kode',
          renderPreview: (language, content, applyPreview) => {
            if (language === "mermaid" && content) {
              return this.mermaidRender(content, applyPreview)
            }
            return null;
          }
        },
/*        [Crepe.Feature.Cursor]: {
          color: '#ff0000',
          width: 2,
          virtual: true,
        },
  */    [Crepe.Feature.LinkTooltip]: {
          inputPlaceholder: 'Indsæt link...',
        },
        [Crepe.Feature.ImageBlock]: {
          inlineUploadButton: "Overfør",
          inlineUploadPlaceholderText: "eller indsæt link",
          blockUploadButton: "Overfør fil",
          blockUploadPlaceholderText: "eller indlæs link",
          blockCaptionPlaceholderText: "Angiv billede tekst",
          blockConfirmButton: "Bekræft",
        },
        [Crepe.Feature.BlockEdit]: {
          textGroup: {
            label: 'Tekst',
            text: { label: 'Tekst' },
            h1: { label: 'Overskrift 1' },
            h2: { label: 'Overskrift 2' },
            h3: { label: 'Overskrift 3' },
            h4: { label: 'Overskrift 4' },
            h5: { label: 'Overskrift 5' },
            h6: { label: 'Overskrift 6' },
            quote: { label: "Citat" },
            divider: { label: "Linje adskiller" },
          },
          listGroup: {
            label: 'Liste',
            bulletList: {
              label: 'Punkter',
            },
            orderedList: {
              label: 'Numereret',
            },
            taskList: {
              label: 'Opgaver',
            },
          },
          advancedGroup: {
            label: 'Advanceret',
            image: { label: 'Billede' },
            codeBlock: { label: 'Kode' },
            table: { label: 'Tabel' },
            math: { label: 'Formel' },
          },
        },
        [Crepe.Feature.Placeholder]: {
          text: '', //'Tast / for at bruge kommandoer',
        },
        [Crepe.Feature.Toolbar]: {
          boldLabel: 'Fed',
          codeLabel: 'Kode',
          italicLabel: 'Kursiv',
          linkLabel: 'Link',
          strikethroughLabel: 'Gennemstreget',
          latexLabel: 'Formel',
          aiLabel: 'AI'
        },
        [Crepe.Feature.TopBar]: {
          headingOptions: [
            { label: 'Tekst', level: null },
            { label: 'Overskrift 1', level: 1 },
            { label: 'Overskrift 2', level: 2 },
            { label: 'Overskrift 3', level: 3 },
            { label: 'Overskrift 4', level: 4 },
            { label: 'Overskrift 5', level: 5 },
            { label: 'Overskrift 6', level: 6 }
          ],
          buildTopBar: (options) => { 
            //this.buildTopBar(options)
          }
        }
      }
    }
  }

  //private latestStatus : EditorStatus;
  private onStatusChange(e: EditorStatus): void {
    //this.latestStatus = e;
    //console.log(this.latestStatus);
  }

  private mermaidRender(content: string, applyPreview: (value: string | HTMLElement) => void): HTMLElement {
    const id = CerRandom.AsString(20)
    const ele = document.createElement("div")
    ele.className = "mermaid"
    ele.id = `mermaid-${id}`
    const svgId = `mermaid-svg-${id}`
    mermaid
      .render(svgId, content)
      .then((output: any) => {
        applyPreview(output.svg)
      })
      .catch((err) => {
        applyPreview(`<span style="color:#DC362E">${err.message}</span>`)
        console.error(err.message)
      })
    return ele;
  }

  //#region Lowlight
  /*
   const lowlight = createLowlight();
      //mermaid.initialize({ startOnLoad: false });
      const tableKit = TableKit.configure({
        // Disable an extension
        tableRow: false,
 
        // Configure an extension
        tableCell: {
          HTMLAttributes: { class: 'list-item' },
        },
      });
  */
  //#endregion Lowlight
 
  //#region Drag handle
  /*
const dragHandle = DragHandle.configure({
  render: () => {
    const element = document.createElement('div')
 
    // Use as a hook for CSS to insert an icon
    element.classList.add('cer-drag-handle')
 
    return element
  },
})      
      this.editor = new Editor({
        element: element,
        extensions: [StarterKit, Markdown, dragHandle,
          //tableKit
          //CodeBlockLowlightMermaid.configure({ lowlight, classList: ["mermaid-container", "diagram"] })
        ],
        content: markdown,
        contentType: 'markdown',
      });
    }
    */
  //#endregion Drag handle

  //#region Top toolbar

    /*
  private buildTopBar(options: GroupBuilder<TopBarItem>) {
    var grp = options.addGroup("close", "Luk");
    grp.addItem("Gem og luk", {
      onRun: (ctx) => { this.onMenuItemSave(ctx) }, 
      active: (ctx) => { return this.isSaveActive(ctx) }, 
      icon: this.iconBtnContentLeft("Gem",'e-save e-grow')
    });
    grp.addItem("Luk", {
      onRun: (ctx) => { this.onMenuItemClose(ctx) }, 
      active: (ctx) => { return true }, 
      icon: this.iconBtnContentLeft("Luk",'e-cancel')
    });
  }
  
  private iconBtnContentLeft(title: string, iconClass: string) : string {
    return `<span class="${iconClass} e-icons e-small e-icon-left"></span><span class="e-text">${title}</span>`;
  }

  private isSaveActive(ctx: Ctx) : boolean {
    return this.editor != null;
  }

  private onMenuItemSave(ctx: Ctx) {
    this.markdown = this.editor.getMarkdown();
    this.isClosed$.next(true);
  }

  private onMenuItemClose(ctx: Ctx) {
    this.isClosed$.next(false);
  }
  */
//#endregion Top toolbar 
}

//#region Codeblock Options
//interface CodeBlockLowlightMermaidOptions extends CodeBlockLowlightOptions {
/**
 * The debounce delay in milliseconds for rendering the mermaid diagram after
 * the user stops typing.
 * @default 300
 */
  //debounce?: number;

/**
 * Configuration options for the mermaid library.
 */
//  mermaidConfig?: MermaidConfig;
/**
 * CSS classes to apply to the container
 */
//  classList: string[] | string;
//}

/*
export const CodeBlockLowlightMermaid =
  CodeBlockLowlight.extend<CodeBlockLowlightMermaidOptions>({
    addOptions() {
      return {
        ...this.parent?.(),
        lowlight: {},
        languageClassPrefix: "language-",
        exitOnTripleEnter: true,
        exitOnArrowDown: true,
        defaultLanguage: null,
        enableTabIndentation: false,
        tabSize: 4,
        HTMLAttributes: {},
        debounce: 300,
        mermaidConfig: {},
        classList: "mermaid-container",
      };
    },
    addAttributes() {
      const parentAttrs = this.parent?.() ?? {};

      return {
        ...parentAttrs,
        id: {
          default: () => `m${crypto.randomUUID().slice(0, 8)}`,
          parseHTML: (element) =>
            element.getAttribute("data-id") ||
            `m${crypto.randomUUID().slice(0, 8)}`,
          renderHTML: (attributes) => {
            if (!(<any>attributes).id) return {};
            return { "data-id": (<any>attributes).id };
          },
        },
      };
    },
    addProseMirrorPlugins() {
      return [
        ...(this.parent?.() || []),
        mermaidPlugin({
          name: this.name,
          lowlight: this.options.lowlight,
          classList: this.options.classList,
          mermaidConfig: this.options.mermaidConfig,
          debounce: this.options.debounce,
        }),
      ];
    },
  });
*/
//#endregion Code block
